import React, { useEffect, useState } from 'react';

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';

import { PROVIDER_OPTIONS_BY_VERTICAL, RuleCondition } from '~/consts/agentHub';

const ALL_PROVIDER_OPTIONS = Object.values(PROVIDER_OPTIONS_BY_VERTICAL).flat();

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function AgentHubCommissionsProviderInput({ setCreatedConditions, existingConditions }: Props) {
  const allowedValues = new Set(ALL_PROVIDER_OPTIONS.map((o) => o.value));
  const [selectedProviders, setSelectedProviders] = useState<Array<string>>(
    (existingConditions?.map((cond) => cond.value as string) || []).filter((v) => allowedValues.has(v)),
  );

  useEffect(() => {
    setCreatedConditions(
      {
        type: 'provider',
        value: selectedProviders.map((provider) => ({
          value: provider,
          label: ALL_PROVIDER_OPTIONS.find((o) => o.value === provider)?.label ?? provider,
        })),
      },
      true,
    );
  }, [setCreatedConditions, selectedProviders]);

  function handleChange(event: SelectChangeEvent<Array<string> | string>) {
    const {
      target: { value },
    } = event;
    setSelectedProviders(typeof value === 'string' ? value.split(',') : value);
  }

  function renderValueText() {
    if (selectedProviders.length === 0) return '';
    if (selectedProviders.length === 1) {
      return ALL_PROVIDER_OPTIONS.find((o) => o.value === selectedProviders[0])?.label ?? selectedProviders[0];
    }
    return `${selectedProviders.length} providers selected`;
  }

  return (
    <Stack gap={2}>
      <FormControl fullWidth>
        <InputLabel id="agent-hub-commissions-provider-input-label">Providers</InputLabel>
        <Select<Array<string>>
          labelId="agent-hub-commissions-provider-input-label"
          multiple
          value={selectedProviders}
          onChange={handleChange}
          input={<OutlinedInput label="Providers" />}
          renderValue={renderValueText}
          sx={{ borderRadius: 0.5 }}
        >
          {ALL_PROVIDER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selectedProviders.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

export default AgentHubCommissionsProviderInput;
